import { useState, useMemo } from 'react';
import type { PrintfulMockup } from '@stamp/merch';
import { MOCK_MOCKUPS, MOCK_VARIANTS } from '../api';

interface MockupGalleryProps {
  mockups: PrintfulMockup[];
  loading?: boolean;
}

interface GroupedMockup {
  variantId: number;
  variantName: string;
  color: string;
  colorHex: string;
  mockupsByPlacement: Record<string, PrintfulMockup>;
}

export function MockupGallery({ mockups, loading }: MockupGalleryProps) {
  const [activeColor, setActiveColor] = useState<string | null>(null);

  const grouped = useMemo<GroupedMockup[]>(() => {
    const byVariant = new Map<number, GroupedMockup>();
    for (const m of mockups) {
      if (!byVariant.has(m.variant_id)) {
        const variant = MOCK_VARIANTS.find((v) => v.id === m.variant_id);
        byVariant.set(m.variant_id, {
          variantId: m.variant_id,
          variantName: variant?.name ?? `Variant ${m.variant_id}`,
          color: variant?.color ?? 'Unknown',
          colorHex: variant?.color_hex ?? '#888888',
          mockupsByPlacement: {},
        });
      }
      byVariant.get(m.variant_id)!.mockupsByPlacement[m.placement] = m;
    }
    return [...byVariant.values()];
  }, [mockups]);

  const colors = grouped.map((g) => g.color);
  const currentColor = activeColor ?? colors[0] ?? null;
  const currentGroup = grouped.find((g) => g.color === currentColor);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p style={{ marginTop: '12px' }}>Generating mockups...</p>
      </div>
    );
  }

  if (mockups.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon">&#128444;</div>
        <div className="empty-state__title">No mockups yet</div>
        <p>Mockups will appear here once they are generated.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="color-swatches" style={{ marginBottom: '16px' }}>
        {colors.map((color) => {
          const g = grouped.find((x) => x.color === color)!;
          return (
            <div
              key={color}
              className={`color-swatch ${currentColor === color ? 'color-swatch--active' : ''}`}
              style={{ background: g.colorHex }}
              onClick={() => setActiveColor(color)}
              title={color}
            />
          );
        })}
      </div>
      <div className="mockup-grid">
        {currentGroup &&
          Object.entries(currentGroup.mockupsByPlacement).map(([placement, m]) => (
            <div key={placement} className="mockup-card">
              <img className="mockup-card__img" src={m.url} alt={`${currentGroup.variantName} ${placement}`} />
              <div className="mockup-card__label">
                <span>{placement.charAt(0).toUpperCase() + placement.slice(1)}</span>
                <span>{currentGroup.variantName}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/** Standalone mockup gallery that uses all available mockups (for demo/testing) */
export function MockupGalleryDemo() {
  return <MockupGallery mockups={MOCK_MOCKUPS} />;
}
